/**
 * T742 — THE EGRESS SEAM. The one module that turns an asset into a URL for a person.
 *
 * ADR-0025 decision 1, quoted because it is the whole contract of this file:
 *
 *   "Exactly one module may resolve an asset into a URL destined for a person, and that
 *    resolution is the only place a delivery host is chosen."
 *
 * ── Why a seam and not a registry ────────────────────────────────────────────────
 *
 * "Can this be blocked?" is not a question about data. It is a question about an EGRESS
 * POINT. Written as a registry it becomes a rule everyone has to remember, and on
 * 2026-09-13 four different places remembered it differently: the guide email, two API
 * responses and a redirect all handed a woman a presigned URL on
 * `*.r2.cloudflarestorage.com` whose two IPs were on that day's Spanish block list, and
 * nothing in the codebase could see it because the host arrived from `wrangler.toml` and
 * every gate reads source. Written as a seam it becomes a place that cannot be bypassed
 * without deleting it.
 *
 * ── Why this file is pure, and `delivery-url.server.ts` is thin (slice 3) ────────
 *
 * The rules live here; reading `env` lives in the adapter. That split is not tidiness — it
 * is what makes "exactly one" TRUE rather than aspirational.
 *
 * Slice 2 put everything in a `.server.ts` module, and React Router keeps those out of the
 * browser bundle by construction. So the three Cloudflare Stream URLs, which are rendered by
 * CLIENT components (`SlotVideo`, `PageVideo`), could not use it and composed their own host
 * instead. Two seams, and the second one existed only because the first could not be reached
 * from the browser. The measured cost of leaving it that way was not a security hole — the
 * Stream host is declared, monitored and in the CSP — it was that **the next delivery surface
 * would have to know which of two modules to use**, which is the exact shape this ADR's own
 * header argues against.
 *
 * So the host-choosing half moved here, where both runtimes can reach it, and the parts that
 * genuinely need a server (`env`, the link base for an email) stay in the adapter. A caller in
 * the browser passes `{}`: every target it can ask for resolves against constants.
 *
 * ── What this module may NOT do ──────────────────────────────────────────────────
 *
 * It may not DECLARE a host. Every host it can return comes from `~/config/hosts` (T424),
 * `~/config/external-origins` (T653) or a declared env var surfaced through
 * `~/config/delivery-hosts` — the derived view, which has no authority of its own either.
 * `isDeliverableUrl` from `./egress-guard` is the seam checking its own output against that
 * set, and is the SAME predicate the runtime backstop applies to a response that never came
 * through here.
 */

import { deliveryHostnamesFrom } from "~/config/delivery-hosts";
import { EXTERNAL_ORIGINS } from "~/config/external-origins";
import { PREVIEW_HOST_SUFFIX } from "~/config/hosts";
import { type Lang } from "~/config/route-config";
import { isDeliverableUrl } from "~/lib/domain/egress-guard";

/**
 * Everything this module needs that is NOT a code constant.
 *
 * Deliberately tiny, and deliberately values rather than an `Env`: an empty object is a valid
 * config, which is what lets a client component call the seam at all. The adapter fills it in
 * from `env` on the server.
 */
export interface DeliveryConfig {
  /**
   * `MEDIA_PUBLIC_URL` for this deployment, when it has one.
   *
   * One field with two roles, and they are the same fact: it is where public catalogue bytes
   * come from AND the one delivery host that is not a code constant. Deriving both from one
   * field is why a host cannot be served from without also being monitored.
   */
  mediaPublicUrl?: string;
  /** `MEDIA_KEY_PREFIX` — the per-environment folder inside the bucket. */
  mediaKeyPrefix?: string;
  /**
   * Absolute base for an `off-site` URL, already resolved and already allowlisted (T424's
   * `resolveLinkBase`). Server-only: an email client has no origin to resolve a path against,
   * and nothing in the browser ever needs one.
   */
  linkBase?: string;
}

/**
 * The guide-download endpoint, per locale — the resource route in `app/routes/descarga.tsx`.
 *
 * Not in `ROUTE_CONFIG` and therefore not reachable through `buildLocalizedPath`, on purpose:
 * it is a byte endpoint, not a navigable surface, so it carries no canonical and no hreflang
 * — the same treatment the offline surface gets, and the same reason. The two paths are
 * registered in `app/routes.ts`; these constants and those registrations must agree, which is
 * what `tests/unit/lib/742-delivery-url-seam.test.ts` checks.
 */
const GUIDE_DOWNLOAD_PATHS: Record<Lang, string> = {
  es: "/es/descarga",
  en: "/en/download",
};

/**
 * What is being handed over. One variant per KIND of asset, not per call site — a new call
 * site that needs a new variant is the moment to check it belongs here at all.
 */
export type DeliveryTarget =
  /**
   * A guide PDF for a LEAD. Resolves to the token-gated download endpoint, which streams the
   * object from the private bucket on our own zone. The token is the authority (no session
   * exists — she has no account); minting it is `~/lib/download-token.server`'s job, not
   * this module's.
   */
  | { kind: "guide-pdf"; lang: Lang; token: string }
  /** Private catalogue media for a MEMBER — the `/media/` gate, where entitlement lives. */
  | { kind: "private-media"; catalogKey: string }
  /** Public catalogue media: the media host when one is configured, the gate otherwise. */
  | { kind: "public-media"; catalogKey: string }
  /**
   * A signed Cloudflare Images URL. One of the two targets that legitimately name a
   * third-party host, which is why `imagesDelivery` is declared in `external-origins.ts` and
   * listed in `MEMBER_DELIVERY_ORIGIN_KEYS` — so the block monitor watches it instead of it
   * hiding the way `R2_S3_ENDPOINT` did.
   */
  | { kind: "signed-image"; path: string }
  /**
   * A Cloudflare Stream video by uid: the player iframe, or the direct MP4 used as the `src`
   * of a decorative `<video>`.
   *
   * The account's customer-subdomain form is mandatory. The legacy generic
   * `iframe.cloudflarestream.com/<uid>` returns the in-player *"This content is blocked"*
   * page for this account — verified 2026-06-14 against the first published hero video, in a
   * clean incognito profile, while the `/watch` URL on the customer subdomain played fine. So
   * it is not signed URLs, allowed origins, nor an extension.
   */
  | { kind: "stream-video"; uid: string; form: "iframe" | "mp4" }
  /**
   * The auto-generated Stream poster for a uid. Cloudflare serves one for every ready video
   * with no extra config, which is what lets a hero show a still immediately — and before
   * "downloads" is enabled on the video, when the MP4 above still 404s. `time` picks the
   * frame; `height` caps the rendered size.
   */
  | { kind: "stream-poster"; uid: string; time?: string; height?: number };

export type DeliveryAudience = "in-page" | "off-site";

/**
 * What the seam decided, with WHICH KIND of URL it is as a discriminant rather than as a
 * fact the caller re-derives from the string.
 *
 * `media.$.tsx` needs that distinction — it redirects when the answer is a real host and
 * falls through to its own gate when the answer is a same-origin path — and the first
 * version of this module made it test `url.startsWith("/")` at three call sites. That is the
 * same "everyone remembers the rule differently" shape the header above argues against, one
 * level down: the seam already knows which kind it produced, so it should say so.
 */
export type DeliveryUrl =
  | { kind: "same-origin-path"; url: string }
  | { kind: "absolute"; url: string };

/** True when the value is already an absolute URL (pre–Ticket-06 catalogue rows). */
export function isLegacyHttpUrl(value: string | null | undefined): boolean {
  return !!value && /^https?:\/\//i.test(value.trim());
}

/** The `public/` prefix is the bucket router — same rule as `media.$.tsx:inferBucketFromKey`. */
export function isPublicCatalogKey(catalogKey: string | null | undefined): boolean {
  return (catalogKey?.trim().toLowerCase() ?? "").startsWith("public/");
}

/** Object path inside R2 including the environment prefix (e.g. `local/videos/...`). */
export function storageObjectKey(config: DeliveryConfig, catalogKey: string): string {
  const raw = catalogKey.trim().replace(/^\/+/, "");
  const prefix = (config.mediaKeyPrefix ?? "").replace(/\/+$/, "");
  return prefix ? `${prefix}/${raw}` : raw;
}

/**
 * The absolute URL of a PUBLIC catalogue object on the configured media host, or `null` when
 * no media host is configured (local, preview) and the gate must serve the bytes itself.
 *
 * A legacy `https://` row passes through untouched. That value is DATA, written into the
 * catalogue before keys existed, so this module cannot be the place that refuses it — it
 * never chose that host. The runtime egress backstop is what sees it (R-T742-4), which is the
 * division of labour ADR-0025 § *Alternatives* describes: write-time gates catch what is
 * written, the backstop catches what arrives at runtime.
 */
export function buildPublicMediaUrl(
  config: DeliveryConfig,
  key: string | null | undefined,
): string | null {
  const k = key?.trim() ?? "";
  if (!k) return null;
  if (isLegacyHttpUrl(k)) return k;
  const base = (config.mediaPublicUrl ?? "").replace(/\/+$/, "");
  if (!base) return null;
  const encoded = storageObjectKey(config, k)
    .split("/")
    .filter(Boolean)
    .map((seg) => encodeURIComponent(seg))
    .join("/");
  return `${base}/${encoded}`;
}

/**
 * Unified `src` for catalogue media (T730).
 *
 * A `public/` key resolves to the media host directly, so the browser never touches `/media/…`
 * for it and the Pages Function is not invoked at all. Everything else keeps the same-origin
 * `/media/…` gate, which is where entitlement lives. An unset media host degrades to the gate
 * rather than rendering a broken image — the gate streams those bytes from the R2 binding in
 * local and preview.
 *
 * Returns `""` for an absent key, because every caller renders it into an `src`/`href` and an
 * empty string is the one value a browser treats as "no request".
 */
export function keyOrUrlToMediaSrc(
  config: DeliveryConfig,
  keyOrUrl: string | null | undefined,
): string {
  const v = keyOrUrl?.trim() ?? "";
  if (!v) return "";
  if (isLegacyHttpUrl(v)) return v;
  if (isPublicCatalogKey(v)) {
    const direct = buildPublicMediaUrl(config, v);
    if (direct) return direct;
  }
  return `/media/${encodeURIComponent(v)}`;
}

/** The hostnames a person may be sent to, for this deployment. */
function allowedHosts(config: DeliveryConfig): string[] {
  return deliveryHostnamesFrom([config.mediaPublicUrl]);
}

/**
 * Is this URL one we are willing to hand a person?
 *
 * A root-relative path always is — it resolves on the host she already reached. An absolute
 * URL must be `https:` and must name a host in the derived delivery set, or a per-PR preview
 * (which no fixed declaration can enumerate).
 */
export function isDeliverableHost(config: DeliveryConfig, url: string): boolean {
  return isDeliverableUrl(url, allowedHosts(config), PREVIEW_HOST_SUFFIX);
}

/**
 * Where a target lives: either an absolute URL of its own, or a path on our own origin.
 *
 * A discriminated union and an EXHAUSTIVE switch rather than two nullable lookups. The first
 * shape of this function returned `string | null` from each half and threw when both came back
 * empty — a branch no input could reach, which is untestable code pretending to be a guard.
 * Here the compiler proves every target has exactly one answer.
 */
type ResolvedTarget = { absolute: string } | { path: string };

function resolveTargetShape(config: DeliveryConfig, target: DeliveryTarget): ResolvedTarget {
  switch (target.kind) {
    case "guide-pdf":
      return { path: `${GUIDE_DOWNLOAD_PATHS[target.lang]}?t=${encodeURIComponent(target.token)}` };
    case "private-media":
      return { path: `/media/${encodeURIComponent(target.catalogKey)}` };
    case "public-media": {
      // The media host when one is configured; otherwise the gate, which streams those bytes
      // from the R2 binding in local and preview. An unset host degrades, it does not break.
      const direct = buildPublicMediaUrl(config, target.catalogKey);
      return direct
        ? { absolute: direct }
        : { path: `/media/${encodeURIComponent(target.catalogKey)}` };
    }
    case "signed-image":
      return { absolute: `${EXTERNAL_ORIGINS.imagesDelivery}${target.path}` };
    case "stream-video":
      return {
        absolute:
          target.form === "iframe"
            ? `${EXTERNAL_ORIGINS.streamDelivery}/${target.uid}/iframe`
            : `${EXTERNAL_ORIGINS.streamDelivery}/${target.uid}/downloads/default.mp4`,
      };
    case "stream-poster": {
      const params = new URLSearchParams();
      if (target.time) params.set("time", target.time);
      if (target.height) params.set("height", String(target.height));
      const query = params.toString();
      const base = `${EXTERNAL_ORIGINS.streamDelivery}/${target.uid}/thumbnails/thumbnail.jpg`;
      return { absolute: query ? `${base}?${query}` : base };
    }
  }
}

/**
 * Resolve an asset into a URL destined for a person.
 *
 * Throws rather than returning a bad value when the result names a host outside the declared
 * set. Failing loudly beats emitting a link that dies in a woman's inbox with nothing to show
 * for it in our logs (ADR-0025 § *The observability constraint*: `ERR_CONNECTION_FAILED` never
 * reaches us).
 */
export function resolveDeliveryUrl(
  config: DeliveryConfig,
  target: DeliveryTarget,
  audience: DeliveryAudience,
): string {
  const shape = resolveTargetShape(config, target);

  let url: string;
  if ("absolute" in shape) {
    url = shape.absolute;
  } else if (audience === "off-site") {
    if (!config.linkBase) {
      // Reachable only from the server, and only by forgetting to supply one — a browser has
      // no reason to ask for an off-site URL at all. Named rather than silently returning a
      // path, because a relative CTA in an email is a dead button (T171 rejects it outright).
      throw new Error(`[delivery-url] "off-site" needs a linkBase; none was configured`);
    }
    url = `${config.linkBase}${shape.path}`;
  } else {
    url = shape.path;
  }

  if (!isDeliverableHost(config, url)) {
    throw new Error(
      `[delivery-url] refused: "${target.kind}" resolved outside the declared delivery set`,
    );
  }
  return url;
}

/**
 * Resolve an asset into a URL, WITH the kind of URL it is.
 *
 * `resolveDeliveryUrl` above is the string-only form for the callers that do not care.
 */
export function resolveDeliveryTarget(
  config: DeliveryConfig,
  target: DeliveryTarget,
  audience: DeliveryAudience,
): DeliveryUrl {
  const url = resolveDeliveryUrl(config, target, audience);
  return url.startsWith("/") ? { kind: "same-origin-path", url } : { kind: "absolute", url };
}
